import { useEffect } from 'react';
import { PersistedExternalDocument } from '@ardoq/api-types';
import { CellEditor } from './types';
import { FlexBox } from '@ardoq/layout';
import { TagButton } from '@ardoq/status-ui';
import { IconName } from '@ardoq/icons';
import styled from 'styled-components';
import { t } from '@ardoq/i18n';

/** Height of the TagButton used for "Attach document" */
const ATTACH_BUTTON_HEIGHT = 24;
/** Gap between document tags and the attach button */
const TAG_GAP = 4;

const DocumentWrapper = styled(FlexBox).attrs({
  width: 'full',
  gap: 'xsmall',
  flexDirection: 'column',
  align: 'start',
  justify: 'center',
})`
  /* same as AG-Grids own padding*/
  padding: 0 15px;
  /* Ensure it sits on top and has a background so the cell underneath doesn't bleed through */
  z-index: 1;
`;
export const LucidchartEditor: CellEditor<
  PersistedExternalDocument[]
> = props => {
  const { commands, accessControl } = props.context;
  const canEdit = accessControl.hasEditPermissionForComponent(props.data);

  /**
   * When the user can edit, we show the "+ Attach document" button which increases
   * the row height. AG Grid doesn't know about this, so we manually add space for
   * the button and restore it when the editor closes.
   */
  useEffect(() => {
    if (!canEdit || !props.value?.length) return;

    const originalHeight = props.node.rowHeight;

    props.node.setRowHeight(
      (props.node.rowHeight ?? 0) + ATTACH_BUTTON_HEIGHT + TAG_GAP
    );
    props.api.onRowHeightChanged();

    return () => {
      props.node.setRowHeight(originalHeight);
      props.api.onRowHeightChanged();
    };
  }, [canEdit, props.api, props.node, props.value]);

  return (
    <DocumentWrapper>
      {props.value?.map(document => {
        return (
          <TagButton
            key={document._id}
            onCloseClick={
              canEdit
                ? () => {
                    props.api.stopEditing();
                    commands.deleteExternalDocument(document);
                  }
                : undefined
            }
            label={document.displayText}
            leftIcon={{ name: IconName.LUCIDCHART }}
            onClick={() => commands.openExternalDocument(document)}
          />
        );
      })}
      {canEdit && (
        <TagButton
          leftIcon={{ name: IconName.ADD }}
          onClick={() => {
            props.api.stopEditing();
            commands.attachExternalDocument(props.data._id);
          }}
        >
          {t(
            props.context.i18nLocale,
            'inventory:lucidchartEditor.attachDocument',
            'Attach document'
          )}
        </TagButton>
      )}
    </DocumentWrapper>
  );
};
